/**
 * The class diagram: the same graph as the nodal view, drawn as UML boxes in layers (see
 * `layout.ts`). This service keeps what must outlive the graph window: the diagram's own pan
 * and zoom (switching views leaves the nodal one as it was), the layout (worked out again only
 * when the graph, the filters or the arrangement change; the filters only hide boxes, never
 * move them), and moving boxes by hand.
 *
 * Boxes moved by hand are part of the graph (`node.diagram`): a drop is one `graph.batch` of
 * `update_node` edits, so it is shared, saved and undone (Ctrl+Z) like any edit. The first box
 * moved fixes the whole automatic picture as the arrangement, so nothing else jumps (undoing
 * it goes back to automatic). While a box is dragged, the other pages see it move live
 * (`project.drag_boxes`, nothing stored). "Arrange by group" tidies the selection, the same way
 * (`arrange.ts`): one undo.
 */
import { createEffect, on } from "solid-js";
import { unwrap } from "solid-js/store";
import type { FieldDef } from "@protocol/FieldDef";
import type { GraphEdit } from "@protocol/GraphEdit";
import type { Graph } from "@protocol/Graph";
import type { Node } from "@protocol/Node";
import type { Actions } from "../../client/actions";
import type { GraphMirror } from "../../client/graph";
import type { PresenceService } from "../presence";
import type { BoxesDragged, Project } from "../project";
import { drawsBoxes, type View } from "../view";
import { arrangeEdits, arrangeLabel, arrangeScope } from "./arrange";
import { allKinds } from "../../shared/kinds";
import { boxFonts, measureBox, type BoxFonts, type BoxShape, type Measure } from "./boxes";
import { layoutDiagram, type BoxPositions, type DiagramLayout } from "./layout";

export { arrangeByGroup, arrangeEdits, arrangeLabel, arrangeScope, type ArrangeScope } from "./arrange";
export { boxFonts, type BoxFonts, type Measure } from "./boxes";
export { blueprintHitAt, boxOfHit, hitAt, tabWidth, type DiagramHit } from "./hits";
export { curve, inputPoint, nextPinId, pinPoint, plusPoint, wireFrom, wiresOf, type PinHit, type Wire } from "./pins";
export { BOX_PAD, PICTURE_H, PIN_LINE, pinText } from "./boxes";
export { NO_FOCUS, countedBundles, selectionFocus, shownArrows, type DiagramBundle, type DiagramFocus, type ShownArrows } from "./bundles";
export { PACKAGE_PAD, PACKAGE_TAB, layoutDiagram, type BoxPositions, type DiagramBox, type DiagramEdge, type DiagramLayout, type DiagramPackage } from "./layout";

/** How far the diagram zooms out and in. */
export const DIAGRAM_ZOOM = { min: 0.05, max: 3, fitMax: 1.4 } as const;

/** What the service needs. */
export interface DiagramDeps {
  graph: GraphMirror;
  view: View;
  project: Project;
  actions: Actions;
  /** Who this page is (its own drags come back from the core and are not someone else's). */
  presence: Pick<PresenceService, "you">;
  /** Measures text (default: a hidden canvas of the page). */
  measure?: Measure;
  /** The theme's font family (default: read from `--font-ui`). */
  fontFamily?: () => string;
  /** The fields of the environment worked in: those marked `on_box` are written in the boxes (default: none). */
  fields?: () => readonly FieldDef[];
}

/** The diagram service. */
export interface Diagram {
  /** Pan and zoom: `screen = world * k + (x, y)`. */
  view: { x: number; y: number; k: number };
  /** Width of the drawing area in CSS pixels (the graph window sets it). */
  width: number;
  /** Height of the drawing area. */
  height: number;
  /** `true` when the drawing is out of date (the window repaints and clears it). */
  redraw: boolean;
  /** `true` once the view was framed (the first time the diagram is shown, it fits). */
  framed: boolean;
  /** The fonts the boxes were measured with (the painter writes with the same). */
  fonts(): BoxFonts;
  /** The diagram now (worked out again when something it depends on changed). */
  layout(): DiagramLayout;
  /** Whether someone arranged the boxes by hand (else the layout is automatic). */
  arranged(): boolean;
  /**
   * The edits fixing every box where the automatic layout draws it (none once someone arranged
   * them): sent first when a box is given a place of its own, so nothing else jumps.
   */
  fixEdits(): GraphEdit[];
  /** A screen point (CSS pixels from the area's corner) in world units. */
  toWorld(x: number, y: number): { x: number; y: number };
  /** Zooms by `factor` keeping the screen point `(x, y)` still. */
  zoomAt(x: number, y: number, factor: number): void;
  /** Moves the view by a number of pixels. */
  pan(dx: number, dy: number): void;
  /** Frames the whole diagram. */
  fit(): void;
  /** Centres the view on a node's box. */
  centerOn(id: string): void;
  /**
   * Moves boxes there (top-left corners) during a drag: shown at once, and to everyone a few
   * times per second. Ends with {@link Diagram.endMove} or {@link Diagram.cancelMove}.
   */
  moveBoxes(positions: BoxPositions): void;
  /** Drops the dragged boxes: one graph edit (one undo); `false` when the core refused it. */
  endMove(): Promise<boolean>;
  /** Puts the dragged boxes back where they were; nothing is changed. */
  cancelMove(): void;
  /** What "Arrange by group" does with the current selection (its button's and menu's label). */
  arrangeLabel(): string;
  /**
   * Arranges the selection by group (`arrange.ts`) and writes the result as positions, so the
   * boxes are free afterwards: one graph edit (one undo), seen by everyone. An action, always
   * offered: `unchanged` when nothing would move, `refused` when the core said no.
   */
  arrangeByGroup(): Promise<"arranged" | "unchanged" | "refused">;
}

/** A text measurer on a hidden canvas, with a guess when the page has no canvas (tests). */
function pageMeasure(): Measure {
  const context = typeof document !== "undefined" ? document.createElement("canvas").getContext("2d") : null;
  return (text, font) => {
    if (!context) return text.length * 7;
    context.font = font;
    return context.measureText(text).width;
  };
}

/** The theme's font family. */
const pageFont = () => (typeof document !== "undefined" ? getComputedStyle(document.documentElement).getPropertyValue("--font-ui").trim() : "") || "sans-serif";

/** The edits putting boxes at `places` (top-left corners, rounded as the core keeps them). */
function placeEdits(places: BoxPositions): GraphEdit[] {
  return Object.entries(places).map(([id, [x, y]]) => ({ edit: "update_node", id, patch: { diagram: [Math.round(x), Math.round(y)] } }));
}

/** Creates the diagram service. */
export function createDiagram({ graph, view, project, actions, presence, measure = pageMeasure(), fontFamily = pageFont, fields = () => [] }: DiagramDeps): Diagram {
  /** Boxes being dragged by this page: they win over everything until the drop is in the graph. */
  let dragging: BoxPositions = {};
  /** Boxes the others are dragging, by person, with when their last news came. */
  const others = new Map<string, { positions: BoxPositions; at: number }>();
  /** Changes when `dragging` or `others` do (the layout is worked out again). */
  let dragVersion = 0;
  let cached: { key: string; layout: DiagramLayout } | null = null;
  /** A drag nobody hears about for this long is over (its page closed, say). */
  const STALE_MS = 4000;
  /** Measured boxes, by node, while the node and the fonts stay the same. */
  const shapes = new WeakMap<object, { font: string; kinds: unknown; known: unknown; fields: unknown; shape: BoxShape }>();

  const diagram: Diagram = {
    view: { x: 0, y: 0, k: 1 },
    width: 1,
    height: 1,
    redraw: true,
    framed: false,
    fonts: () => boxFonts(fontFamily()),
    layout() {
      const s = view.state;
      const fonts = diagram.fonts();
      const key = [graph.state.revision, graph.state.version, s.hiddenCategories.join("\u0001"), JSON.stringify(s.linkKinds), s.groups, s.bundles && view.mode() !== "blueprint", dragVersion, fonts.name, fields().map((f) => (f.on_box ? f.id : "")).join("\u0001")].join("|");
      if (cached && cached.key === key) return cached.layout;
      const moving: BoxPositions = {};
      for (const other of others.values()) Object.assign(moving, other.positions);
      Object.assign(moving, dragging);
      const layout = layoutDiagram(unwrap(graph.state.graph) as Graph, filters(), moving, shapeOf);
      cached = { key, layout };
      diagram.redraw = true;
      return layout;
    },
    arranged: () => graph.state.graph.nodes.some((n) => !!n.diagram),
    toWorld: (x, y) => ({ x: (x - diagram.view.x) / diagram.view.k, y: (y - diagram.view.y) / diagram.view.k }),
    zoomAt(x, y, factor) {
      const v = diagram.view;
      const k = Math.min(Math.max(v.k * factor, DIAGRAM_ZOOM.min), DIAGRAM_ZOOM.max);
      v.x = x - ((x - v.x) * k) / v.k;
      v.y = y - ((y - v.y) * k) / v.k;
      v.k = k;
      diagram.redraw = true;
    },
    pan(dx, dy) {
      diagram.view.x += dx;
      diagram.view.y += dy;
      diagram.redraw = true;
    },
    fit() {
      const b = diagram.layout().bounds;
      const v = diagram.view;
      if (!b) {
        Object.assign(v, { x: diagram.width / 2, y: diagram.height / 2, k: 1 });
      } else {
        const margin = 40;
        const k = Math.min(Math.max(Math.min((diagram.width - 2 * margin) / b.w, (diagram.height - 2 * margin) / b.h), DIAGRAM_ZOOM.min), DIAGRAM_ZOOM.fitMax);
        Object.assign(v, { k, x: diagram.width / 2 - (b.x + b.w / 2) * k, y: diagram.height / 2 - (b.y + b.h / 2) * k });
      }
      diagram.framed = true;
      diagram.redraw = true;
    },
    fixEdits: () => (diagram.arranged() ? [] : placeEdits(diagram.layout().positions)),
    centerOn(id) {
      const box = diagram.layout().byId.get(id);
      if (!box) return;
      const v = diagram.view;
      v.x = diagram.width / 2 - (box.x + box.w / 2) * v.k;
      v.y = diagram.height / 2 - (box.y + box.h / 2) * v.k;
      diagram.redraw = true;
    },
    moveBoxes(positions) {
      dragging = { ...dragging, ...positions };
      dragVersion++;
      project.dragBoxes(positions, false);
      diagram.redraw = true;
    },
    async endMove() {
      const dropped = dragging;
      if (!Object.keys(dropped).length) return true;
      // The first box moved fixes the whole automatic picture, so nothing else jumps.
      const edits = [...diagram.fixEdits().filter((e) => !(e.edit === "update_node" && e.id in dropped)), ...placeEdits(dropped)];
      const done = await actions.batch(edits);
      project.dragBoxes({}, true);
      // Held where it was dropped until the graph says so (or, refused, back where it was).
      if (dragging === dropped) {
        dragging = {};
        dragVersion++;
        diagram.redraw = true;
      }
      return !!done;
    },
    cancelMove() {
      if (!Object.keys(dragging).length) return;
      dragging = {};
      dragVersion++;
      project.dragBoxes({}, true);
      diagram.redraw = true;
    },
    arrangeLabel: () => arrangeLabel(arrangeScope(view.state.selection, graph.state.graph), graph.state.graph),
    async arrangeByGroup() {
      const g = unwrap(graph.state.graph) as Graph;
      const scope = arrangeScope(view.state.selection, g);
      const edits = arrangeEdits(g, diagram.layout(), scope, shapeOf);
      if (!edits.length) return "unchanged";
      return (await actions.batch(edits)) ? "arranged" : "refused";
    },
  };

  /** The filters the diagram follows (the view's). */
  function filters() {
    const s = view.state;
    // A blueprint draws every wire on its own: a wire hidden in a bundle could not be dragged.
    return { hiddenCategories: s.hiddenCategories, linkKinds: s.linkKinds, groups: s.groups, bundles: s.bundles && view.mode() !== "blueprint" };
  }
  /** A node's box, measured once per node object and font. */
  function shapeOf(node: Node): BoxShape {
    const fonts = diagram.fonts();
    const kinds = graph.state.graph.kinds;
    const known = graph.state.known;
    const shown = fields();
    const kept = shapes.get(node);
    if (kept && kept.font === fonts.name && kept.kinds === kinds && kept.known === known && kept.fields === shown) return kept.shape;
    const shape = measureBox(node, fonts, measure, allKinds(unwrap(known), unwrap(kinds)), shown);
    shapes.set(node, { font: fonts.name, kinds, known, fields: shown, shape });
    return shape;
  }

  // The others' drags: shown live, dropped when they end or go quiet.
  project.onBoxesDragged((dragged: BoxesDragged) => {
    if (dragged.by === presence.you() || dragged.view === "board") return;
    if (dragged.done) others.delete(dragged.by);
    else others.set(dragged.by, { positions: { ...others.get(dragged.by)?.positions, ...dragged.positions }, at: Date.now() });
    dragVersion++;
    diagram.redraw = true;
  });
  setInterval(() => {
    const now = Date.now();
    for (const [by, other] of others) if (now - other.at > STALE_MS) others.delete(by) && dragVersion++;
  }, 1000);
  // In the diagram, "fit" and "centre on" move the diagram's view (the nodal one stays put).
  createEffect(on(view.fitRequests, () => drawsBoxes(view.mode()) && diagram.fit(), { defer: true }));
  createEffect(on(view.centerRequest, (request) => request && drawsBoxes(view.mode()) && diagram.centerOn(request.id), { defer: true }));
  return diagram;
}
